import React, { useEffect, useRef } from "react";

/**
 * Lightweight canvas confetti burst. No external dependency.
 * Renders once when `fire` transitions to truthy, then auto-clears.
 * Pointer-events disabled so it never interferes with the UI beneath.
 */
export default function Confetti({ fire, duration = 1600, colors = ["#CCFF00", "#FF9E80", "#D4B3FF", "#FFC6FF", "#A8E6FF", "#FFF7C2", "#FF3B30"] }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!fire) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Emit two bursts from left-ish and right-ish origins so the whole
    // area gets covered.
    const N = 90;
    const particles = [];
    for (let i = 0; i < N; i++) {
      const side = i < N / 2 ? 0 : 1;
      const originX = side === 0 ? w * 0.25 : w * 0.75;
      const originY = h * 0.35;
      const angle = (side === 0 ? -1 : 1) * (Math.PI / 3) + (Math.random() - 0.5) * 0.9;
      const speed = 5 + Math.random() * 8;
      particles.push({
        x: originX,
        y: originY,
        vx: Math.sin(angle) * speed,
        vy: -Math.cos(angle) * speed - Math.random() * 2,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.35,
        size: 6 + Math.random() * 6,
        color: colors[i % colors.length],
        life: 0,
        maxLife: duration + Math.random() * 400,
        shape: Math.random() > 0.5 ? "rect" : "circle",
      });
    }

    const start = performance.now();
    const gravity = 0.28;
    const drag = 0.995;

    const tick = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, w, h);
      let alive = 0;
      for (const p of particles) {
        p.vy += gravity;
        p.vx *= drag;
        p.vy *= drag;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life = elapsed;
        if (p.life > p.maxLife || p.y > h + 40) continue;
        alive++;
        const alpha = Math.max(0, 1 - p.life / p.maxLife);
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      if (alive > 0) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, w, h);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ctx.clearRect(0, 0, w, h);
    };
  }, [fire, duration, colors]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[80]"
      aria-hidden
      data-testid="confetti-canvas"
    />
  );
}
